import { type Component, For, Show, createSignal, onCleanup, onMount } from "solid-js";

import type { AppRoute } from "@/appRoutes";

// Locked five-route navigation (handoff §3 wordmark invariant +
// CTO-approved expansion). Sign out is appended below the divider.
const NAV_ITEMS: readonly { route: AppRoute; label: string }[] = [
  { route: "chat", label: "Chat" },
  { route: "dashboard", label: "Dashboard" },
  { route: "compute", label: "Compute" },
  { route: "cli", label: "CLI" },
  { route: "settings", label: "Settings" },
] as const;

interface AppHeaderProps {
  currentRoute: AppRoute;
  authenticated: boolean;
  onNavigate: (next: AppRoute) => void;
  onOpenPalette: () => void;
  onLogout?: () => void;
}

// Minimal neuro header per handoff §3: wordmark left, ⌘K hint right.
// Pixel-identical across every state. Clicking the wordmark opens a
// dropdown nav (CTO direction 2026-05-26). Anonymous users get the
// dropdown closed and only see the wordmark/⌘K — no nav, no chrome.
const AppHeader: Component<AppHeaderProps> = (props) => {
  const [open, setOpen] = createSignal(false);
  let menuEl: HTMLDivElement | undefined;

  onMount(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!open()) return;
      if (!menuEl) return;
      if (!e.target) return;
      if (menuEl.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open()) setOpen(false);
    };
    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onKey);
    onCleanup(() => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onKey);
    });
  });

  const select = (route: AppRoute) => {
    setOpen(false);
    props.onNavigate(route);
  };

  return (
    <header class="neuro-header">
      <div class="neuro-header-left" ref={menuEl}>
        <button
          type="button"
          class="neuro-wordmark"
          aria-haspopup="menu"
          aria-expanded={open()}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
        >
          z01
        </button>
        <Show when={open() && props.authenticated}>
          <div class="neuro-wordmark-menu" role="menu">
            <For each={NAV_ITEMS}>
              {(item) => (
                <button
                  type="button"
                  role="menuitem"
                  class={`neuro-wordmark-item${
                    props.currentRoute === item.route ? " is-active" : ""
                  }`}
                  onClick={() => select(item.route)}
                >
                  {item.label}
                </button>
              )}
            </For>
            <Show when={props.onLogout}>
              <div class="neuro-wordmark-divider" />
              <button
                type="button"
                role="menuitem"
                class="neuro-wordmark-item"
                onClick={() => {
                  setOpen(false);
                  props.onLogout?.();
                }}
              >
                Sign out
              </button>
            </Show>
          </div>
        </Show>
      </div>
      <button
        type="button"
        class="neuro-palette-hint"
        onClick={props.onOpenPalette}
        aria-label="Open command palette"
      >
        ⌘K
      </button>
    </header>
  );
};

export default AppHeader;
